// Hideo - Creators page (Tools › Creators): the creators you follow, split into Collaborators and Competitors (stored
// type values 'Collaboratore' / 'Competitor', never translated: shown with tv() or a label). A creator with both types
// appears in both lists. The creator dialogs: CreatorDialog.vue (detail), CreatorEditDialog.vue (edit) and
// AnalysedVideoDialog.vue (src/components/dialogs/), opened through the creators store (src/stores/creators.ts).
import type { AnalyzedVideo, Creator, CreatorType, CreatorVerdict, ProfileData, VideoToAnalyse, VideoVerdict } from '../data/types'
import { normUrl, ytThumb } from '../utils/url'
import { validProject } from './projects'
import { putItem, removeItem } from '../data/items'

/** Stored creator types, in the order of the page buttons and of a saved creator's `types` */
export const CREATOR_TYPE_KEYS: readonly CreatorType[] = ['Collaboratore', 'Competitor']

/** Types of a creator: old or broken data without any counts as Competitor */
export const creatorTypes = (c: Pick<Creator, 'types'>): CreatorType[] => (Array.isArray(c.types) && c.types.length ? c.types : ['Competitor'])

/** The other list ("also competitor" / "also collaborator" in the rows) */
export const otherType = (type: CreatorType): CreatorType => (type === 'Competitor' ? 'Collaboratore' : 'Competitor')

/** Creators of one type, in their saved order (the faces on the page buttons) */
export const ofType = <T extends Pick<Creator, 'types'>>(list: T[], type: CreatorType): T[] => list.filter(c => creatorTypes(c).includes(type))

interface CreatorList {
  /** Every creator of the type, by name */
  all: Creator[]
  /** Those matching the search (all of them without a search) */
  shown: Creator[]
}

/** The open list: creators of a type sorted by name, filtered by the search text (in the name, case-insensitive) */
export function creatorList(list: Creator[], type: CreatorType, query: string, locale?: string): CreatorList {
  const all = ofType(list, type).sort((a, b) => (a.name || '').localeCompare(b.name || '', locale))
  const q = query.trim().toLowerCase()
  return { all, shown: q ? all.filter(c => (c.name || '').toLowerCase().includes(q)) : all }
}

/** Letter shown when a creator has no photo */
export const creatorInitial = (c: Pick<Creator, 'name'>): string => (c.name || 'C').charAt(0).toUpperCase()

/** A verdict shown in green ('Promosso'); any other in red */
export const verdictOk = (verdict: string): boolean => /Promosso/.test(verdict)

// ===== Creator detail (CreatorDialog.vue) =====
/** The creator card in the detail has something to show (otherwise it invites to fill it in) */
export const creatorCardFilled = (c: Pick<Creator, 'bio' | 'strengths' | 'weaknesses' | 'verdict'>): boolean =>
  !!(c.bio || c.strengths || c.weaknesses || c.verdict)

/** Picture of an analysed video in the list: its own thumbnail, otherwise the YouTube one of its link ('' = none) */
export const analysedThumb = (v: Pick<AnalyzedVideo, 'thumbnail' | 'link'>): string => v.thumbnail || ytThumb(v.link)

// ===== Creator edit dialog (CreatorEditDialog.vue) =====
/** The fields of the creator dialog */
interface CreatorForm {
  name: string
  /** Stored types chosen (at least one) */
  types: CreatorType[]
  projectId: string
  /** '' | "media:<id>" (the photo, stored apart) */
  avatar: string
  /** '' | "media:<id>" | http(s) address (the banner behind the photo, stored apart) */
  banner: string
  youtube: string
  instagram: string
  tiktok: string
  twitch: string
  /** With @mentions as "@[type:id]" (src/features/mentions.ts) */
  bio: string
  strengths: string
  weaknesses: string
  verdict: CreatorVerdict
  /** With @mentions */
  notes: string
}

/** The dialog's starting fields: those of the creator, or empty for a new one with the given stored type (by default
 *  Competitor) and the project of new items (newProject) */
export function creatorForm(d: ProfileData, id: string | null, type: string, newProject: string): CreatorForm {
  const c = id ? d.creators.find(x => x.id === id) : null
  if (c) {
    return {
      name: c.name || '', types: [...creatorTypes(c)], projectId: validProject(d, c.projectId, newProject), avatar: c.avatar || '', banner: c.banner || '',
      youtube: c.youtube || '', instagram: c.instagram || '', tiktok: c.tiktok || '', twitch: c.twitch || '',
      bio: c.bio || '', strengths: c.strengths || '', weaknesses: c.weaknesses || '', verdict: c.verdict || '', notes: c.notes || ''
    }
  }
  const t = (CREATOR_TYPE_KEYS as readonly string[]).includes(type) ? (type as CreatorType) : 'Competitor'
  return {
    name: '', types: [t], projectId: newProject, avatar: '', banner: '', youtube: '', instagram: '', tiktok: '', twitch: '',
    bio: '', strengths: '', weaknesses: '', verdict: '', notes: ''
  }
}

/** Saves the dialog into the data: a new creator (id null) or the creator with that id, replaced by the dialog's fields
 *  in the stored order (name and texts trimmed, social links with "https://" added, types in CREATOR_TYPE_KEYS order),
 *  keeping its analysed videos and videos to analyse. null when the name is empty (nothing changes). created = a new creator. The caller saves. */
export function saveCreator(d: ProfileData, id: string | null, f: CreatorForm, now = Date.now()): { creator: Creator; created: boolean } | null {
  const name = f.name.trim()
  if (!name) return null
  const old = id ? d.creators.find(x => x.id === id) : null
  const creator: Creator = {
    id: id || 'c_' + now,
    name,
    types: CREATOR_TYPE_KEYS.filter(type => f.types.includes(type)),
    projectId: f.projectId,
    avatar: f.avatar,
    banner: f.banner,
    youtube: normUrl(f.youtube),
    instagram: normUrl(f.instagram),
    tiktok: normUrl(f.tiktok),
    twitch: normUrl(f.twitch),
    notes: f.notes.trim(),
    bio: f.bio.trim(),
    strengths: f.strengths.trim(),
    weaknesses: f.weaknesses.trim(),
    verdict: f.verdict,
    analyzedVideos: id ? ((old && old.analyzedVideos) || []) : [],
    toAnalyse: id ? ((old && old.toAnalyse) || []) : []
  }
  return { creator, created: putItem(d.creators, creator, !!id) }
}

/** Removes a creator (and its analysed videos) from the data (true if it was there). The caller saves. */
export function deleteCreator(d: ProfileData, id: string): boolean {
  return removeItem(d, 'creators', id)
}

// ===== Analysed video dialog (AnalysedVideoDialog.vue) =====
/** The fields of the analysed video dialog */
interface AnalysedVideoForm {
  title: string
  link: string
  /** '' | "media:<id>" (uploaded) | http(s) image address */
  thumbnail: string
  strengths: string
  weaknesses: string
  /** With @mentions */
  analysis: string
  verdict: VideoVerdict
}

/** The analysed video of a creator, or undefined */
export const findAnalysed = (d: ProfileData, creatorId: string, id: string | null): AnalyzedVideo | undefined =>
  id ? (d.creators.find(x => x.id === creatorId)?.analyzedVideos || []).find(x => x.id === id) : undefined

/** The dialog's starting fields: those of the analysed video, or empty; a new one made from a video to analyse
 *  (todoId) starts with its title and link */
export function analysedForm(d: ProfileData, creatorId: string, id: string | null, todoId: string | null = null): AnalysedVideoForm {
  const v = findAnalysed(d, creatorId, id)
  const todo = v ? undefined : findToAnalyse(d, creatorId, todoId)
  return {
    title: v?.title || todo?.title || '', link: v?.link || todo?.link || '', thumbnail: v?.thumbnail || '', strengths: v?.strengths || '',
    weaknesses: v?.weaknesses || '', analysis: v?.analysis || '', verdict: v?.verdict || ''
  }
}

/** Saves the dialog into the creator's analysed videos: replaces the one with that id, otherwise adds it (with that id,
 *  or a new one). Link with "https://" added, texts trimmed. null when the title is empty or the creator is gone
 *  (nothing changes). The caller saves. */
export function saveAnalysed(d: ProfileData, creatorId: string, id: string | null, f: AnalysedVideoForm, now = Date.now()): AnalyzedVideo | null {
  const title = f.title.trim()
  const c = d.creators.find(x => x.id === creatorId)
  if (!title || !c) return null
  c.analyzedVideos = c.analyzedVideos || []
  const v: AnalyzedVideo = {
    id: id || 'av_' + now, title,
    link: normUrl(f.link), thumbnail: f.thumbnail,
    strengths: f.strengths.trim(),
    weaknesses: f.weaknesses.trim(),
    analysis: f.analysis.trim(),
    verdict: f.verdict
  }
  const i = c.analyzedVideos.findIndex(x => x.id === id)
  if (i !== -1) c.analyzedVideos[i] = v
  else c.analyzedVideos.push(v)
  return v
}

/** Removes an analysed video from its creator (true if it was there). The caller saves. */
export function deleteAnalysed(d: ProfileData, creatorId: string, id: string): boolean {
  const c = d.creators.find(x => x.id === creatorId)
  if (!c) return false
  const n = (c.analyzedVideos || []).length
  c.analyzedVideos = (c.analyzedVideos || []).filter(x => x.id !== id)
  return c.analyzedVideos.length !== n
}

// ===== Videos to analyse (CreatorDialog.vue) =====
/** An analysed video with no analysis, strengths or weaknesses yet: it is shown among the videos to analyse */
export const analysedEmpty = (v: Pick<AnalyzedVideo, 'analysis' | 'strengths' | 'weaknesses'>): boolean =>
  !(v.analysis || '').trim() && !(v.strengths || '').trim() && !(v.weaknesses || '').trim()

/** The creator's analysed videos that are filled in (analysis, strengths or weaknesses) */
export const analysedDone = (c: Pick<Creator, 'analyzedVideos'>): AnalyzedVideo[] => (c.analyzedVideos || []).filter(v => !analysedEmpty(v))

/** How many videos of the creator are still to analyse: the links in its list plus the analysed videos still empty */
export const toAnalyseCount = (c: Pick<Creator, 'toAnalyse' | 'analyzedVideos'>): number =>
  (c.toAnalyse || []).length + (c.analyzedVideos || []).filter(analysedEmpty).length

/** A video to analyse of a creator, or undefined */
export const findToAnalyse = (d: ProfileData, creatorId: string, id: string | null): VideoToAnalyse | undefined =>
  id ? (d.creators.find(x => x.id === creatorId)?.toAnalyse || []).find(x => x.id === id) : undefined

/** Adds a video to analyse at the end of the creator's list (link with "https://" added, texts trimmed). null when
 *  both link and title are empty or the creator is gone (nothing changes). The caller saves. */
export function addToAnalyse(d: ProfileData, creatorId: string, f: { link: string; title: string }, now = Date.now()): VideoToAnalyse | null {
  const c = d.creators.find(x => x.id === creatorId)
  const link = normUrl(f.link), title = f.title.trim()
  if (!c || (!link && !title)) return null
  c.toAnalyse = c.toAnalyse || []
  let id = 'ta_' + now
  while (c.toAnalyse.some(x => x.id === id)) id += '_'
  const v: VideoToAnalyse = { id, link, title }
  c.toAnalyse.push(v)
  return v
}

/** Removes a video to analyse from its creator (true if it was there). The caller saves. */
export function removeToAnalyse(d: ProfileData, creatorId: string, id: string): boolean {
  const c = d.creators.find(x => x.id === creatorId)
  if (!c) return false
  const n = (c.toAnalyse || []).length
  c.toAnalyse = (c.toAnalyse || []).filter(x => x.id !== id)
  return c.toAnalyse.length !== n
}
